// Typed entry while drawing: a distance, an angle or coordinates typed at the
// pointer for the draft's next point (parseEntry), its live preview and its
// commit. docs/claude/tools.md.
import { type Vec2 } from '../../shared/types';
import {
  flashStatus,
  invalidate,
  state,
  updateStatus,
  vp,
} from './core';
import { commitTypedUnderWay } from './underWayUi';
import {
  drawingDimStyle,
  formatDimension,
} from '../dimFormat';
import { parseMeasure } from './panelRows';
import { addPoint } from '../polylineDraft';
import { paperToScreen } from '../sheets';
import {
  fromPlaneFrame,
  PLAN_PLANE,
  toPlaneFrame,
} from '../workPlane';
import {
  $,
  activePolyline,
  finishPick,
  finishRect,
  finishRectRoom,
  pickBase,
  pickPlane,
  placeOf,
  rectRoomStart,
  rectStart,
} from '../main';
import { typeOffsetDistance } from './modifyTools';
import {
  activeSheet,
  paperView,
} from './sheetSpace';
import { schHover } from './sheetSchematic';

// ---- Direct distance entry while drawing a polygon/wall/line vertex ----
/** The active vertex-drawing tool's draft and its last (anchor) vertex, if any.
 *  On a sheet that is the schematic line draft, so typed distance / coordinate
 *  entry drives it exactly as it drives the model tools. */
export function drawDraft(): { draft: Vec2[]; prev: Vec2 } | null {
  const draft = activePolyline()?.draft.pts ?? null;
  if (!draft || draft.length < 1) return null;
  return { draft, prev: draft[draft.length - 1] };
}

/**
 * The space the active vertex-drawing tool works in — model world (Y-up mm) or
 * the active sheet's paper (Y-down mm). One accessor, so the typed-entry chip,
 * the pointing direction and the draft preview can never disagree about which
 * one is live.
 */
function draftSpace(): { hover: Vec2 | null; toScreen: (p: Vec2) => Vec2 } {
  if (activeSheet()) {
    const pv = paperView();
    return { hover: schHover, toScreen: (p) => paperToScreen(pv, p.x, p.y) };
  }
  return { hover: state.hoverWorld, toScreen: (p) => vp.worldToScreen(p) };
}

/** For the two-click box tools (rectangle / rectangular room): the placed first
 *  corner and how to finish the box once the opposite corner is known. Lets a
 *  typed coordinate drive the second corner just like a polygon vertex. */
export function pendingRectCorner(): { start: Vec2; commit: (opposite: Vec2) => void } | null {
  if (pickBase && placeOf(state.tool).picks) return { start: pickBase, commit: (p) => finishPick(p) };
  if (state.tool === 'rectangle' && rectStart) { const s = rectStart; return { start: s, commit: (b) => finishRect(s, b) }; }
  if (state.tool === 'rect-room' && rectRoomStart) { const s = rectRoomStart; return { start: s, commit: (b) => finishRectRoom(s, b) }; }
  return null;
}

/** The anchor a typed entry is measured from: the last draft vertex, or the
 *  first corner of a two-click box. */
function typedAnchor(): Vec2 | null {
  return drawDraft()?.prev ?? pendingRectCorner()?.start ?? null;
}

/** Parse a distance like "150", "-1.5m", "12 cm", "3ft", "10in" to millimetres.
 *  A bare number is millimetres (the app-wide default); a unit suffix (mm / cm /
 *  m / ft / in) is converted. Returns null if it isn't a valid measurement. */
function parseDistanceMm(s: string): number | null {
  return parseMeasure(s, 'mm');
}

/**
 * Resolve a typed entry to a world point during drawing. Supports, in AutoCAD
 * style (numbers default to millimetres, with mm / cm / m / ft / in suffixes
 * accepted; angles in degrees):
 *   - `150`         distance along the current pointing direction from `prev`
 *   - `x,y`         absolute coordinates
 *   - `@dx,dy`      coordinates relative to `prev`
 *   - `d<a` / `@d<a`  polar: distance `d` at angle `a` from `prev`
 * `dir` is the pointing direction (hover − prev). Null if unresolvable.
 *
 * `yUp` is false in a sheet's paper space, where Y runs DOWN the page: the forms
 * that carry a direction (the polar angle and a relative dy) are flipped so that
 * "90°" and "a positive dy" mean UP on screen in both spaces. An absolute pair is
 * left alone — that one is read straight off the page.
 */
export function parseEntry(raw: string, prev: Vec2 | null, dir: Vec2 | null, yUp = true): Vec2 | null {
  let str = raw.trim();
  let rel = false;
  if (str.startsWith('@')) { rel = true; str = str.slice(1); }
  const sy = yUp ? 1 : -1;
  const lt = str.indexOf('<');
  if (lt >= 0) { // polar distance<angle (always relative to prev)
    const d = parseDistanceMm(str.slice(0, lt));
    const a = parseFloat(str.slice(lt + 1).replace(',', '.'));
    if (d == null || !isFinite(a) || !prev) return null;
    const rad = (a * Math.PI) / 180;
    return { x: prev.x + Math.cos(rad) * d, y: prev.y + sy * Math.sin(rad) * d };
  }
  const comma = str.indexOf(',');
  if (comma >= 0) { // coordinate pair
    const cx = parseDistanceMm(str.slice(0, comma));
    const cy = parseDistanceMm(str.slice(comma + 1));
    if (cx == null || cy == null) return null;
    if (rel) return prev ? { x: prev.x + cx, y: prev.y + sy * cy } : null;
    return { x: cx, y: cy };
  }
  const mm = parseDistanceMm(str); // bare distance along the pointing direction
  if (mm == null || !prev || !dir) return null;
  const len = Math.hypot(dir.x, dir.y);
  if (len < 1e-6) return null;
  return { x: prev.x + (dir.x / len) * mm, y: prev.y + (dir.y / len) * mm };
}

/** The vertex implied by the current typed entry (see `parseEntry`), typed in
 *  the frame of the plane the pick works in: in a section view x along the view,
 *  y up it. */
export function distEntryPoint(): Vec2 | null {
  if (state.distEntry == null) return null;
  const plane = pickBase ? pickPlane : PLAN_PLANE;
  const raw = typedAnchor(), hover = draftSpace().hover;
  const prev = raw && toPlaneFrame(plane, raw), h = hover && toPlaneFrame(plane, hover);
  const q = parseEntry(state.distEntry, prev, prev && h ? { x: h.x - prev.x, y: h.y - prev.y } : null, !activeSheet());
  return q && fromPlaneFrame(plane, q);
}

/** The point the next click would put: the typed one, else the cursor's — what
 *  every draft's and pick's preview shows, so it agrees with what commits
 *  (ROTATE's typed value is an angle, not a point). */
export function nextPoint(): Vec2 | null {
  return (state.tool === 'rotate' ? null : distEntryPoint()) ?? state.hoverWorld;
}

/** A length on the cursor's chip: in the model, in the drawing's dimension
 *  style (dimFormat.ts, as the dimensions and the handles' figures); on a
 *  sheet, the paper's mm. */
function readoutLength(mm: number): string {
  return activeSheet() ? `${Math.round(mm)} mm` : formatDimension(mm, drawingDimStyle(state.drawing));
}

/** Show/position/hide the distance-entry chip near the cursor. When nothing is
 *  being typed it doubles as a live length + angle readout for the segment in
 *  progress. */
export function updateDistEntry(): void {
  const el = $('dist-entry');
  // A typed distance / coordinate shows in the command bar (AutoCAD's command
  // line); the chip at the cursor keeps only the live length / angle readout.
  // A bar the user is typing in is theirs — never overwritten from here.
  const cmd = $<HTMLInputElement>('cmd-input');
  if (document.activeElement !== cmd) {
    if (state.distEntry != null) { cmd.value = state.distEntry; cmd.classList.add('typed-entry'); }
    else if (cmd.classList.contains('typed-entry')) { cmd.value = ''; cmd.classList.remove('typed-entry'); }
  }
  let text: string | null = null;
  const space = draftSpace();
  const hover = space.hover;
  if (state.distEntry == null) {
    const ctx = drawDraft();
    const box = ctx ? null : pendingRectCorner();
    if (ctx && hover) {
      const dx = hover.x - ctx.prev.x, dy = hover.y - ctx.prev.y;
      const len = Math.hypot(dx, dy);
      if (len > 1e-6) {
        // Paper Y runs DOWN the page, so the angle is negated there to read the
        // way it does in the model (counter-clockwise from east).
        let ang = (Math.atan2(activeSheet() ? -dy : dy, dx) * 180) / Math.PI;
        if (ang < 0) ang += 360;
        text = `${readoutLength(len)}  ∠${ang.toFixed(1)}°`;
      }
    } else if (box && hover) {
      const dx = Math.abs(hover.x - box.start.x), dy = Math.abs(hover.y - box.start.y);
      if (dx > 1e-6 || dy > 1e-6) text = `${readoutLength(dx)} × ${readoutLength(dy)}`;
    }
  }
  if (text == null || !state.dynInput) { el.classList.add('hidden'); return; }
  el.textContent = text;
  el.classList.add('readout');
  if (hover) {
    const s = space.toScreen(hover);
    el.style.left = `${s.x + 16}px`;
    el.style.top = `${s.y - 30}px`;
  }
  el.classList.remove('hidden');
}

/** Confirm the typed entry: place the implied vertex on the active draft, or
 *  finish a two-click box (rectangle / rectangular room) at the implied corner. */
export function commitDistEntry(): void {
  // What is under way (a handle's or a grip's work) takes its figure.
  if (commitTypedUnderWay()) return;
  if (state.tool === 'offset' && state.distEntry != null) {
    const raw = state.distEntry;
    state.distEntry = null;
    updateDistEntry();
    typeOffsetDistance(raw);
    updateStatus();
    invalidate();
    return;
  }
  // ROTATE's typed value is an ANGLE (degrees CCW), not a distance.
  if (state.tool === 'rotate' && pickBase && state.distEntry != null) {
    const deg = Number(state.distEntry.replace(',', '.').replace(/°$/, ''));
    state.distEntry = null;
    updateDistEntry();
    if (Number.isFinite(deg)) finishPick(null, deg);
    return;
  }
  const pt = distEntryPoint();
  const ctx = drawDraft();
  const pl = activePolyline();
  const box = pendingRectCorner();
  state.distEntry = null;
  updateDistEntry();
  if (pt) {
    // A typed point is a click's, exactly on (to rounding): on the first point it closes.
    if (ctx && pl) { const r = addPoint(pl.rule, pl.draft, pt, 1e-6); if ('refused' in r) flashStatus(r.refused); }
    else if (box) { box.commit(pt); return; } // finishRect/finishRectRoom sets tool + clears state
  }
  invalidate();
}
